// Upload.js (Redesigned with Drag & Drop + Manual Upload)
import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';

export default function Upload() {
  const navigate = useNavigate();
  const fileInputRef = useRef();
  const [fileName, setFileName] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleFile = (file) => {
    if (!file.name.endsWith('.csv') && !file.name.endsWith('.xlsx')) {
      setError('Only .csv or .xlsx files are supported');
      return;
    }
    setError(null);
    setFileName(file.name);
    setUploading(true);
    setProgress(0);
    // Simulate upload progress
    let percent = 0;
    const interval = setInterval(() => {
      percent += Math.random() * 20 + 10;
      setProgress(Math.min(100, Math.floor(percent)));
      if (percent >= 100) {
        clearInterval(interval);
        setUploading(false);
        setTimeout(() => {
          navigate('/field-mapping', {
            state: {
              fileName: file.name,
              mockHeaders: ['First Name', 'Last Name', 'Email', 'Phone'],
              sampleData: [{
                'First Name': 'John',
                'Last Name': 'Doe',
                'Email': 'john.doe@example.com',
                'Phone': '1234567890'
              }]
            }
          });
        }, 400);
      }
    }, 300);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files.length > 0) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = () => setDragActive(false);

  const handleManualUpload = () => {
    if (fileInputRef.current?.files.length) {
      handleFile(fileInputRef.current.files[0]);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <Header />
      <div className="max-w-2xl mx-auto px-4 py-12 text-center">
        <h1 className="text-3xl font-bold text-slate-800 mb-4">Upload Your Address File</h1>
        <p className="text-slate-600 mb-8">Drag & drop a CSV/XLSX file or use the manual upload below.</p>
        {uploading && (
          <div className="flex flex-col items-center justify-center mb-6">
            <div className="w-12 h-12 border-4 border-amber-400 border-t-transparent rounded-full animate-spin mb-2"></div>
            <div className="text-amber-700 font-semibold">Uploading... {progress}%</div>
          </div>
        )}

        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`border-4 border-dashed rounded-xl py-20 px-6 transition-all duration-300 ${dragActive ? 'border-amber-600 bg-amber-50' : 'border-slate-300 bg-white'}`}
        >
          <p className="text-slate-500">Drag and drop your file here</p>
        </div>

        <div className="mt-6 text-sm text-slate-500">OR</div>

        <div className="mt-6">
          <input
            type="file"
            ref={fileInputRef}
            accept=".csv,.xlsx"
            className="hidden"
            onChange={handleManualUpload}
          />
          <button
            className="bg-amber-600 hover:bg-amber-700 text-white px-6 py-2 rounded-full shadow transition-all"
            onClick={() => fileInputRef.current?.click()}
          >
            Choose File Manually
          </button>
        </div>

        {error && <p className="text-red-500 mt-4">{error}</p>}

        {fileName && <p className="mt-4 text-green-700">Selected File: <strong>{fileName}</strong></p>}
      </div>
    </div>
  );
}
